import * as React from "react";
import { Dialog, DialogSize } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type ConfirmDialogOptions = {
  title: string;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  size?: DialogSize;
  className?: string;
};

export type ConfirmDialogChoice = "confirm" | "cancel" | "dismiss";

type ConfirmState = ConfirmDialogOptions & {
  open: boolean;
  resolve: ((value: ConfirmDialogChoice) => void) | null;
};

let state: ConfirmState = {
  open: false,
  title: "",
  resolve: null,
};

const listeners = new Set<(s: ConfirmState) => void>();

function setState(next: ConfirmState) {
  state = next;
  listeners.forEach(listener => listener(state));
}

export function confirmChoice(options: ConfirmDialogOptions): Promise<ConfirmDialogChoice> {
  return new Promise(resolve => {
    setState({
      open: true,
      title: options.title,
      description: options.description,
      confirmLabel: options.confirmLabel ?? "Yes",
      cancelLabel: options.cancelLabel ?? "No",
      destructive: options.destructive ?? false,
      size: options.size ?? "sm",
      className: options.className,
      resolve,
    });
  });
}

export function confirmDialog(options: ConfirmDialogOptions): Promise<boolean> {
  return confirmChoice(options).then(result => result === "confirm");
}

function close(result: ConfirmDialogChoice) {
  state.resolve?.(result);
  setState({
    ...state,
    open: false,
    resolve: null,
  });
}

export function ConfirmDialogProvider() {
  const [currentState, setCurrentState] = React.useState<ConfirmState>(state);

  React.useEffect(() => {
    const listener = (s: ConfirmState) => setCurrentState({ ...s });
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  const handleOpenChange = React.useCallback((open: boolean) => {
    if (!open) {
      close("dismiss");
    }
  }, []);

  const handleConfirm = React.useCallback(() => {
    close("confirm");
  }, []);

  const handleCancel = React.useCallback(() => {
    close("cancel");
  }, []);

  if (!currentState.open) {
    return null;
  }

  return (
    <Dialog
      open={currentState.open}
      onOpenChange={handleOpenChange}
      size={currentState.size}
      className={cn("gap-0", currentState.className)}
    >
      <Dialog.Header>
        <Dialog.Title>{currentState.title}</Dialog.Title>
      </Dialog.Header>
      <Dialog.Content>{currentState.description}</Dialog.Content>
      <Dialog.Footer>
        <Button variant="outline" onClick={handleCancel}>
          {currentState.cancelLabel}
        </Button>
        <Button variant={currentState.destructive ? "destructive" : "default"} onClick={handleConfirm}>
          {currentState.confirmLabel}
        </Button>
      </Dialog.Footer>
    </Dialog>
  );
}
